import { Tag, TagProps } from 'antd'
import dayjs from 'dayjs'
import { T } from 'govwell-ui'
import styled from 'styled-components'
import {
  InspectionCoordinationType,
  TimeZone,
  RecordTaskInspectionAttemptOverviewFragment,
  RecordTaskInspectionAttemptLogEntryFragment,
} from 'types/graphql'

import { getFontSize, TextSize } from 'src/components/Typography/Text'

import { Size } from '../../../../../govwell-ui/types'
import { DateFormats, formatMoment } from '../../../../../utils/date'

const InlineTag = styled(Tag)<{ $size?: Size }>`
  height: fit-content;
  width: fit-content;
  max-width: 100%;
  white-space: normal;
  word-break: break-word;

  ${({ $size }) => {
    switch ($size) {
      case 'xs':
        return `
          padding: 0px 1px;
          > span {
            font-size: ${getFontSize(TextSize.ExtraSmall)}px;
            line-height: ${getFontSize(TextSize.ExtraSmall)}px;
          }
        `
      case 'sm':
        return `
          padding: 1px 2px;
          > span {
            font-size: ${getFontSize(TextSize.Small)}px;
            line-height: ${getFontSize(TextSize.Small)}px;
          }
        `
    }
    return ''
  }};
`

type Props = {
  attempt: RecordTaskInspectionAttemptOverviewFragment
  inspectionCoordinationType: InspectionCoordinationType | null | undefined
  isAvailable: boolean
  showDate?: boolean
  showTime?: boolean
  size?: Size
  timeZone: TimeZone | null | undefined
  logEntry?: RecordTaskInspectionAttemptLogEntryFragment
}
const TableCellInspectionStatus = (props: Props) => {
  const { label, tagColor } = getInspectionStatusDisplayConfig(props)
  return (
    <InlineTag $size={props.size} color={tagColor}>
      <T.Body className="text-inherit" maxLines={props.showDate ? 2 : 1}>
        {label || '-'}
      </T.Body>
    </InlineTag>
  )
}

export default React.memo(TableCellInspectionStatus)

type ShouldShowInspectionTimeToApplicantArgs = {
  inspectionSchedulingShouldShowTimesToApplicants: boolean
  isGov: boolean
}

export const shouldShowInspectionTimeToApplicant = ({
  isGov,
  inspectionSchedulingShouldShowTimesToApplicants,
}: ShouldShowInspectionTimeToApplicantArgs): boolean =>
  isGov || inspectionSchedulingShouldShowTimesToApplicants

export const getInspectionStatusDisplayConfig = (
  props: Props
): {
  label: string | undefined
  tagColor: TagProps['color']
} => {
  const {
    attempt,
    isAvailable,
    logEntry,
    showDate,
    showTime = true,
    inspectionCoordinationType = 'SchedulingRequest',
    timeZone,
  } = props

  const dateOnly = !showTime

  const scheduledStr = attempt.calendarEvent?.details?.moment
    ? formatMoment(
        attempt.calendarEvent.details.moment,
        DateFormats.MonthDayYearWithSlash,
        timeZone,
        { dateOnly }
      )
    : ''

  const requestedStr = attempt.calendarEvent?.proposal?.moment
    ? formatMoment(
        attempt.calendarEvent.proposal.moment,
        DateFormats.MonthDayYearWithSlash,
        timeZone,
        { dateOnly }
      )
    : ''

  const status = attempt?.status
  const result = logEntry?.result ?? attempt?.result
  if (!isAvailable && !status) {
    return { label: 'Not Available Yet', tagColor: undefined }
  }

  switch (status) {
    case 'Scheduled': {
      const now = dayjs()
      const scheduledDate =
        attempt.calendarEvent?.details?.moment?.__typename === 'CalendarEventMomentDate'
          ? dayjs(attempt.calendarEvent.details.moment.date)
          : attempt.calendarEvent?.details?.moment?.__typename === 'CalendarEventMomentDateTime'
            ? dayjs(attempt.calendarEvent.details.moment.endTime)
            : null
      const isToday = scheduledDate?.isSame(now, 'day')
      if (scheduledDate?.isBefore(now) && !isToday) {
        return {
          label: showDate ? `Overdue (Scheduled: ${scheduledStr})` : 'Overdue',
          tagColor: 'gold',
        }
      }
      return {
        label: showDate
          ? `Scheduled: ${isToday && dateOnly ? 'Today' : scheduledStr}`
          : 'Scheduled',
        tagColor: 'purple',
      }
    }
    case 'Cancelled': {
      return { label: 'Cancelled', tagColor: 'red' }
    }
    case 'Requested': {
      return { label: showDate ? `Requested for ${requestedStr}` : 'Requested', tagColor: 'pink' }
    }
    case 'Done': {
      return {
        label: result?.name,
        tagColor: result?.isPassing ? 'green' : 'red',
      }
    }
    case 'Draft': {
      return { label: 'Draft', tagColor: 'default' }
    }
    default: {
      return {
        label:
          inspectionCoordinationType === 'SchedulingRequest' ? 'Not Requested' : 'Not Scheduled',
        tagColor: 'blue',
      }
    }
  }
}
